import { useState, useEffect } from "react";
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonInput,
  IonTextarea,
  IonLabel,
  IonToggle,
  IonAvatar,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import { close, saveOutline } from "ionicons/icons";
import { useQueryClient } from "@tanstack/react-query";
import { usePymbleApi } from "../../hooks/usePymbleApi";
import { useDictionary } from "../../hooks/useDictionary";
import { useSelectIconPopover } from "../../hooks/useSelectIconPopover";
import { displayMsg } from "pymble-ui/components";
import { parseISO, formatISO } from "date-fns";

import "./CreateGroupModal.css";

interface GroupTypeEntry {
  TypeId?: number;
  Name?: string;
  [key: string]: unknown;
}

interface CreateGroupModalProps {
  onDismiss: () => void;
  onCreated: (groupId: string) => void;
}

const toISO = (dateStr: string | null): string | null => {
  if (!dateStr) return null;
  try {
    return formatISO(parseISO(dateStr));
  } catch {
    return null;
  }
};

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  onDismiss,
  onCreated,
}) => {
  const { callApi } = usePymbleApi();
  const queryClient = useQueryClient();
  const { dictionary: groupTypes, dictionaryLoaded } =
    useDictionary("GroupTypes");
  const { presentIcons, selectedIcon, IconsPopover } =
    useSelectIconPopover();

  const [saving, setSaving] = useState(false);

  // Form state
  const [nameShort, setNameShort] = useState("");
  const [nameLong, setNameLong] = useState("");
  const [descShort, setDescShort] = useState("");
  const [descLong, setDescLong] = useState("");
  const [icon, setIcon] = useState("");
  const [typeId, setTypeId] = useState<number | undefined>();
  const [typeLabel, setTypeLabel] = useState("");
  const [showMemberList, setShowMemberList] = useState(false);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [carerSubscribe, setCarerSubscribe] = useState(false);
  const [staffSubscribe, setStaffSubscribe] = useState(false);
  const [studentSubscribe, setStudentSubscribe] = useState(false);

  useEffect(() => {
    if (selectedIcon?.img) {
      setIcon(selectedIcon.img);
    }
  }, [selectedIcon]);

  const handleCreate = async () => {
    if (!nameShort.trim()) {
      displayMsg("Name Short is required", {
        type: "warning",
        position: "top",
        duration: 2000,
      });
      return;
    }
    if (!descShort.trim()) {
      displayMsg("Description Short is required", {
        type: "warning",
        position: "top",
        duration: 2000,
      });
      return;
    }
    if (!typeId) {
      displayMsg("Group Type is required", {
        type: "warning",
        position: "top",
        duration: 2000,
      });
      return;
    }
    if (!startAt) {
      displayMsg("Start At is required", {
        type: "warning",
        position: "top",
        duration: 2000,
      });
      return;
    }

    setSaving(true);
    try {
      const canSubscribe: string[] = [];
      if (carerSubscribe) canSubscribe.push("parent");
      if (staffSubscribe) canSubscribe.push("staff");
      if (studentSubscribe) canSubscribe.push("student");

      const data = {
        Name: { Short: nameShort, Long: nameLong },
        Description: { Short: descShort, Long: descLong },
        Icon: icon || undefined,
        Type: {
          Id: parseInt(String(typeId)),
          Label: typeLabel,
        },
        StartAt: toISO(startAt),
        EndAt: toISO(endAt),
        ShowMemberList: showMemberList,
        CanSubscribe: canSubscribe,
      };

      const result = (await callApi("POST", "/group/admin", {
        data,
      })) as { _id?: string } | null;

      queryClient.invalidateQueries({ queryKey: ["groups"] });

      if (result?._id) {
        displayMsg("Group created", {
          type: "success",
          position: "top",
          duration: 1500,
        });
        onCreated(result._id);
      } else {
        displayMsg("Group created but could not get ID", {
          type: "warning",
          position: "top",
          duration: 3000,
        });
        onDismiss();
      }
    } catch {
      displayMsg("Failed to create group", {
        type: "danger",
        position: "top",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <IonHeader>
        <IonToolbar color="light">
          <IonTitle>New Group</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={handleCreate}
              disabled={saving}
              color="secondary"
              fill="solid"
              className="ion-text-capitalize"
              size="small"
            >
              <IonIcon slot="start" icon={saveOutline} />
              {saving ? "Creating..." : "Create"}
            </IonButton>
            <IonButton onClick={onDismiss} fill="clear" size="small">
              <IonIcon slot="icon-only" icon={close} size="small" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="create-group-content">
        <IonGrid>
          <IonRow>
            <IonCol size="12">
              <IonItem>
                <IonInput
                  label="Name Short *"
                  labelPlacement="stacked"
                  value={nameShort}
                  onIonInput={(e) => setNameShort(e.detail.value || "")}
                  placeholder="Short name for the group"
                />
              </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="12">
              <IonItem>
                <IonInput
                  label="Name Long"
                  labelPlacement="stacked"
                  value={nameLong}
                  onIonInput={(e) => setNameLong(e.detail.value || "")}
                  placeholder="Full name for the group"
                />
              </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="12">
              <IonItem>
                <IonInput
                  label="Description Short *"
                  labelPlacement="stacked"
                  value={descShort}
                  onIonInput={(e) => setDescShort(e.detail.value || "")}
                  placeholder="Short description"
                />
              </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="12">
              <IonItem>
                <IonTextarea
                  label="Description Long"
                  labelPlacement="stacked"
                  value={descLong}
                  onIonInput={(e) => setDescLong(e.detail.value || "")}
                  placeholder="Full description"
                  rows={4}
                />
              </IonItem>
            </IonCol>
          </IonRow>

          {/* Icon + Group Type */}
          <IonRow className="ion-align-items-center">
            <IonCol size="auto">
              <IonItem lines="none">
                <IonLabel color="primary">Icon</IonLabel>
                <IonAvatar
                  className="create-group-icon-avatar"
                  onClick={(e) => presentIcons({ event: e.nativeEvent })}
                >
                  {icon ? (
                    <img src={icon} alt="group icon" />
                  ) : (
                    <div className="create-group-icon-placeholder">+</div>
                  )}
                </IonAvatar>
              </IonItem>
            </IonCol>
            <IonCol>
              <IonItem lines="none">
                <IonLabel color="primary" position="stacked">
                  Group Type *
                </IonLabel>
                {dictionaryLoaded ? (
                  <IonSelect
                    value={typeId}
                    onIonChange={(e) => {
                      const id = e.detail.value;
                      setTypeId(id);
                      const gtype = (groupTypes as GroupTypeEntry[]).find(
                        (t) => t.TypeId === id
                      );
                      setTypeLabel(gtype?.Name ?? "");
                    }}
                    placeholder="Select Type"
                    interface="popover"
                  >
                    {(groupTypes as GroupTypeEntry[]).map((gt) => (
                      <IonSelectOption key={gt.TypeId} value={gt.TypeId}>
                        {gt.Name}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                ) : (
                  <IonLabel color="medium">Loading types...</IonLabel>
                )}
              </IonItem>
            </IonCol>
          </IonRow>

          {/* Show Member List */}
          <IonRow>
            <IonCol size="12">
              <IonItem lines="none">
                <IonLabel color="primary">Show Member List</IonLabel>
                <IonToggle
                  checked={showMemberList}
                  onIonChange={() => setShowMemberList(!showMemberList)}
                  color="secondary"
                />
              </IonItem>
            </IonCol>
          </IonRow>

          {/* Dates */}
          <IonRow>
            <IonCol size="6">
              <IonItem>
                <IonInput
                  label="Start At *"
                  labelPlacement="stacked"
                  type="date"
                  value={startAt}
                  onIonInput={(e) => setStartAt(e.detail.value || "")}
                />
              </IonItem>
            </IonCol>
            <IonCol size="6">
              <IonItem>
                <IonInput
                  label="End At"
                  labelPlacement="stacked"
                  type="date"
                  value={endAt}
                  onIonInput={(e) => setEndAt(e.detail.value || "")}
                />
              </IonItem>
            </IonCol>
          </IonRow>

          {/* Who can subscribe */}
          <IonRow>
            <IonCol size="12">
              <div className="create-group-section-label">
                Who can subscribe?
              </div>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <IonItem lines="none">
                <IonLabel>Carer</IonLabel>
                <IonToggle
                  checked={carerSubscribe}
                  onIonChange={() => setCarerSubscribe(!carerSubscribe)}
                  color="secondary"
                />
              </IonItem>
            </IonCol>
            <IonCol>
              <IonItem lines="none">
                <IonLabel>Staff</IonLabel>
                <IonToggle
                  checked={staffSubscribe}
                  onIonChange={() => setStaffSubscribe(!staffSubscribe)}
                  color="secondary"
                />
              </IonItem>
            </IonCol>
            <IonCol>
              <IonItem lines="none">
                <IonLabel>Student</IonLabel>
                <IonToggle
                  checked={studentSubscribe}
                  onIonChange={() => setStudentSubscribe(!studentSubscribe)}
                  color="secondary"
                />
              </IonItem>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>

      {IconsPopover}
    </>
  );
};

export default CreateGroupModal;
